/**
 * The words for a social-media post: the cover slide's lines and the caption,
 * from the boat's own data. German, deterministic, editable afterwards — no
 * figure is invented, every number comes from the record.
 */
import type { CoverText } from './render'

export type Occasion = 'new' | 'price' | 'sold' | 'none'

export interface SocialBoat {
  id: number
  name?: string | null
  manufacturer?: string | null
  model?: string | null
  build_year?: string | number | null
  boat_type?: string | null
  length_total?: number | null
  width?: number | null
  draft_max?: number | null
  asking_price?: number | null
  /** "Angebotspreis": when set, the price to show instead of the asking price. */
  offer_price?: number | null
  location_town?: string | null
  translated_marketing_description?: Record<string, string> | null
  status?: string | null
}

export interface Office {
  phone?: string | null
  email?: string | null
}

const BADGE: Record<Occasion, string> = {
  new: 'Neu im Bestand',
  price: 'Preis reduziert',
  sold: 'Verkauft',
  none: '',
}

const HEADLINE: Record<Occasion, string> = {
  new: 'Neu im Bestand',
  price: 'Neuer Preis',
  sold: 'Verkauft',
  none: '',
}

const TYPE: Record<string, string> = { sailboat: 'Segelyacht', motorboat: 'Motoryacht' }

const de = (n: number, digits = 2) => n.toLocaleString('de-DE', { maximumFractionDigits: digits })

/** "Hallberg-Rassy 342" — maker and model, or the boat's name. */
export const boatTitle = (b: SocialBoat) =>
  [b.manufacturer, b.model].filter(Boolean).join(' ').trim() || b.name || `Boot ${b.id}`

/** The price to show: the offer price ("Angebotspreis") if set, else the asking price. */
export const shownPrice = (b: SocialBoat) =>
  [b.offer_price, b.asking_price].find((p): p is number => typeof p === 'number' && p > 0) ?? null

export const priceText = (b: SocialBoat) => {
  const price = shownPrice(b)
  return price ? `${de(price, 0)} €` : ''
}

export function factsLine(b: SocialBoat) {
  const parts: string[] = []
  if (b.build_year) parts.push(`Baujahr ${b.build_year}`)
  if (b.length_total) parts.push(`${de(b.length_total)} m`)
  if (b.boat_type && TYPE[b.boat_type]) parts.push(TYPE[b.boat_type])
  return parts.join(' · ')
}

export function coverText(b: SocialBoat, occasion: Occasion): CoverText {
  return {
    title: boatTitle(b),
    facts: factsLine(b),
    // A sold boat's price is nobody's business any more.
    price: occasion === 'sold' ? '' : priceText(b),
    badge: BADGE[occasion],
  }
}

/**
 * The first sentences of the German description, up to about `max`
 * characters, stopping before anything that looks like contact details —
 * those belong at the end of the caption, once, and correct.
 */
export function teaser(description: string | undefined | null, max = 320) {
  if (!description) return ''
  const text = description
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .trim()
  const contact = /(telefon|tel\.|mobil|e-?mail|@|\(at\)|www\.|https?:|besichtigung|besuchen sie)/i
  const out: string[] = []
  let length = 0
  for (const raw of text.split(/(?<=[.!?])\s+|\n+/)) {
    const sentence = raw.replace(/^[•\-–*]\s*/, '').trim()
    if (!sentence) continue
    if (contact.test(sentence)) break
    if (length + sentence.length > max && out.length > 0) break
    out.push(sentence)
    length += sentence.length + 1
  }
  return out.join(' ')
}

const tag = (text: string | null | undefined) =>
  text
    ? '#' +
      text
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/ß/g, 'ss')
        .replace(/[^A-Za-z0-9]+/g, '')
        .toLowerCase()
    : null

export function hashtags(b: SocialBoat) {
  const tags = [
    '#yachthandel24',
    tag(b.manufacturer),
    b.boat_type === 'sailboat' ? '#segelyacht' : b.boat_type === 'motorboat' ? '#motoryacht' : null,
    b.boat_type === 'sailboat' ? '#segeln' : b.boat_type === 'motorboat' ? '#motorboot' : null,
    '#gebrauchtboot',
    '#bootkaufen',
    '#yachtverkauf',
    tag(b.location_town?.split(/[/,]/)[0]),
    '#ostsee',
  ].filter((t): t is string => !!t && t.length > 2)
  return Array.from(new Set(tags)).join(' ')
}

/** The full caption: occasion, boat, a teaser, the facts, how to get in touch, tags. */
export function caption(b: SocialBoat, occasion: Occasion, office: Office = {}) {
  const lines: string[] = []
  const headline = HEADLINE[occasion]
  lines.push(headline ? `${headline}: ${boatTitle(b)}` : boatTitle(b))

  const intro = teaser(b.translated_marketing_description?.de)
  if (intro) lines.push('', intro)

  const facts: string[] = []
  if (b.build_year) facts.push(`Baujahr: ${b.build_year}`)
  if (b.length_total) {
    facts.push(
      `Maße: ${de(b.length_total)} × ${b.width ? de(b.width) : '–'} m` +
        (b.draft_max ? `, Tiefgang ${de(b.draft_max)} m` : '')
    )
  }
  if (b.location_town) facts.push(`Liegeplatz: ${b.location_town}`)
  if (occasion !== 'sold' && priceText(b)) facts.push(`Preis: ${priceText(b)}`)
  if (facts.length) lines.push('', ...facts)

  lines.push(
    '',
    occasion === 'sold'
      ? 'Danke an Käufer und Verkäufer! Sie möchten Ihr Boot auch verkaufen? Sprechen Sie uns an.'
      : 'Besichtigung nach Vereinbarung. Mehr Bilder und alle Daten auf yachthandel24.de.'
  )
  const contact = [office.phone, office.email].filter(Boolean).join(' · ')
  if (contact) lines.push(contact)

  lines.push('', hashtags(b))
  return lines.join('\n')
}
